import { type MotionProps, motion } from "framer-motion";
import { type FC, type ReactNode, useId, useRef } from "react";
import { createPortal } from "react-dom";

import { useFocusTrap } from "../../hooks/useFocusTrap";
import { useOverlay } from "../../hooks/useOverlay";
import { Button } from "./Button";
import { OverlayFooter } from "./OverlayFooter";
import { Text } from "./Text";

interface ModalProps {
  title: string;
  subtitle?: ReactNode;
  /** Compact metadata line below subtitle, rendered as truncating monospace text. */
  meta?: ReactNode;
  /** Icon rendered to the left of the title. */
  icon?: ReactNode;
  /** Custom header left-side content; replaces `icon` + `title` + `subtitle` + `meta` when provided. */
  headerContent?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  widthClass?: string;
  /** Enable framer-motion animation for the overlay + panel. */
  useMotion?: boolean;
  overlayMotionProps?: MotionProps;
  panelMotionProps?: MotionProps;
  overlayClassName?: string;
  panelClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  showCloseButton?: boolean;
}

// Overlay sits at z-[65]: above both inspector sheet tiers (base z-50,
// overlay z-[60]) — modals are blocking dialogs and must never render under
// a sheet — while staying below transient tooltips/popovers at z-[70].
const OVERLAY_BASE =
  "fixed inset-0 z-[65] flex items-center justify-center bg-black/50 backdrop-blur-sm p-0 md:p-3";
const PANEL_BASE =
  "glass-panel relative flex h-full max-h-[100vh] w-full flex-col overflow-hidden rounded-none shadow-lg md:h-auto md:max-h-[88vh]";

const cls = (...values: (string | undefined | false)[]): string =>
  values.filter(Boolean).join(" ");

/**
 * The ARIA contract of the dialog panel, in one place so both render paths
 * (plain and framer-motion) carry identical semantics.
 *
 * Normally the rendered `<h3>` is the name source (`aria-labelledby`), which
 * keeps the accessible name and the visible title from drifting. When a caller
 * supplies `headerContent` it replaces that heading entirely, so pointing at a
 * nonexistent id would leave the dialog nameless — those fall back to
 * `aria-label` from the still-required `title` prop.
 */
export function buildModalDialogProps(
  titleId: string,
  hasHeaderContent: boolean,
  title: string,
): {
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-modal": true;
  role: "dialog";
} {
  return {
    "aria-modal": true,
    role: "dialog",
    ...(hasHeaderContent
      ? { "aria-label": title }
      : { "aria-labelledby": titleId }),
  };
}

export const Modal: FC<ModalProps> = ({
  title,
  subtitle,
  meta,
  icon,
  headerContent,
  children,
  onClose,
  footer,
  widthClass = "max-w-3xl",
  useMotion = false,
  overlayMotionProps,
  panelMotionProps,
  overlayClassName,
  panelClassName,
  headerClassName,
  bodyClassName,
  showCloseButton = true,
}) => {
  useOverlay(true, onClose);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  // Semantics + focus containment (F13). NOT background inertness: see
  // useFocusTrap's scope note — content behind the overlay stays in the
  // accessibility tree.
  useFocusTrap(panelRef, true);
  const dialogProps = buildModalDialogProps(
    titleId,
    headerContent != null,
    title,
  );

  const bodyClassNames = cls(
    "panel-body flex-1 overflow-y-auto p-2.5 md:p-3",
    bodyClassName,
  );

  const panelContent = (
    <>
      <div
        className={cls(
          "panel-header flex-shrink-0 px-3 py-2 md:px-4 md:py-2.5",
          headerClassName,
        )}
      >
        {headerContent ?? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {icon && (
              <span className="flex-shrink-0 text-slate-500 dark:text-slate-400">
                {icon}
              </span>
            )}
            <div className="min-w-0">
              <h3
                id={titleId}
                className="truncate font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white"
              >
                {title}
              </h3>
              {subtitle && (
                <Text as="div" className="truncate">
                  {subtitle}
                </Text>
              )}
              {meta && (
                <div className="truncate font-mono text-xs text-slate-400 dark:text-slate-500">
                  {meta}
                </div>
              )}
            </div>
          </div>
        )}
        {showCloseButton && (
          <div className="flex-shrink-0">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </div>

      {useMotion ? (
        <motion.div layoutScroll className={bodyClassNames}>
          {children}
        </motion.div>
      ) : (
        <div className={bodyClassNames}>{children}</div>
      )}

      {footer && <OverlayFooter>{footer}</OverlayFooter>}
    </>
  );

  if (!useMotion) {
    return createPortal(
      <div
        className={cls(OVERLAY_BASE, "animate-fadeIn", overlayClassName)}
        onClick={onClose}
      >
        <div
          {...dialogProps}
          ref={panelRef}
          tabIndex={-1}
          className={cls(
            PANEL_BASE,
            "animate-slideUp",
            widthClass,
            panelClassName,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {panelContent}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <motion.div
      {...overlayMotionProps}
      layoutRoot
      className={cls(OVERLAY_BASE, overlayClassName)}
      onClick={onClose}
    >
      <motion.div
        {...panelMotionProps}
        {...dialogProps}
        ref={panelRef}
        tabIndex={-1}
        className={cls(PANEL_BASE, widthClass, panelClassName)}
        onClick={(e) => e.stopPropagation()}
      >
        {panelContent}
      </motion.div>
    </motion.div>,
    document.body,
  );
};
